import DataTableReferrals from "@/components/data-table-referrals/data-table-referrals";
import { CardReferralDiscounts } from "@/components/referral/card-referral-discounts";
import { CardReferralInvitation } from "@/components/referral/card-referral-invitation";
import { CardReferralLink } from "@/components/referral/card-referral-link";
import { CardReferralSendLead } from "@/components/referral/card-referral-send-lead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToastError } from "@/components/ui/use-toast";
import { useUser } from "@/contexts/user-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { ReferralDTO } from "@shaarp/common";
import { InfoIcon } from "lucide-react";
import { useEffect, useState } from "react";

export default function ReferralsPage() {
  const { t } = useTranslation();
  const toastError = useToastError();
  const api = useApi();
  const { user } = useUser();

  const [referrals, setReferrals] = useState<ReferralDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchReferrals();
  }, []);

  const fetchReferrals = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get<ReferralDTO[]>("/my/user/referrals");
      setReferrals(data);
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const hasReferralDiscounts =
    user?.referralMonthlyDiscount || user?.referralAnnualDiscount;

  const hasReferralLink = !!user?.referralCode;

  return (
    <div className="container flex-1 p-8 pt-6 space-y-5">
      <div className="flex flex-col mb-5 space-y-2 md:flex-row md:items-start md:justify-between md:space-y-0">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            {t("referrals.page.title")}
          </h2>
          <p className="flex items-center text-muted-foreground">
            {t("referrals.page.description")}
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger>
                  <InfoIcon className="ml-2 w-4 h-4" />
                </TooltipTrigger>
                <TooltipContent>{t("referrals.page.tooltip")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </p>
        </div>
        <div className="flex justify-end mt-4 w-full md:w-auto md:mt-0">
          <Card className="w-auto">
            <CardContent className="flex flex-col gap-3 justify-center items-center py-2 sm:flex-row sm:gap-6">
              <div className="flex flex-col flex-1 items-center min-w-0 text-center">
                <span className="text-xs text-muted-foreground">
                  {t("referrals.offers.perAccount.label")}
                </span>
                <span className="mt-1 text-2xl font-bold text-primary">
                  {t("referrals.offers.perAccount.amount", { amount: 100 })}
                </span>
                <span className="mt-1 text-[10px] text-muted-foreground">
                  {t("referrals.offers.perAccount.amountDescription", {
                    months: 3,
                  })}
                </span>
              </div>
              <span className="px-2 text-sm font-medium text-muted-foreground">
                {t("referrals.offers.or")}
              </span>
              <div className="flex flex-col flex-1 items-center min-w-0 text-center">
                <span className="text-xs text-muted-foreground">
                  {t("referrals.offers.revenue.label")}
                </span>
                <span className="mt-1 text-2xl font-bold text-primary">
                  {t("referrals.offers.revenue.amount", { amount: 20 })}
                </span>
                <span className="mt-1 text-[10px] text-muted-foreground">
                  {t("referrals.offers.revenue.amountDescription", {
                    months: 6,
                  })}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Separator className="my-6" />

      <div className="flex flex-col gap-6 mt-8 lg:flex-row lg:flex-nowrap">
        {hasReferralDiscounts && (
          <div className="flex-1 min-w-0">
            <CardReferralDiscounts />
          </div>
        )}

        {hasReferralLink && (
          <div className="flex-1 min-w-0">
            <CardReferralLink />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <CardReferralInvitation onSendSuccess={fetchReferrals} />
        </div>

        <div className="flex-1 min-w-0">
          <CardReferralSendLead />
        </div>
      </div>
      <div className="flex gap-6 items-start mt-8 w-full">
        <div className="w-full">
          <Card>
            <CardHeader>
              <CardTitle className="flex flex-1">
                {t("referrals.dataTable.title")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DataTableReferrals referrals={referrals} isLoading={isLoading} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
